/**
 * WealthCommand — Sprint 22.7.30 (Module 8 decision-journal + alpha-attribution).
 *
 * Surfaces /api/wealth: the decision journal (signals + KAIROS recommendation
 * actions), behavioral summary by regime, and outcome-based alpha attribution
 * (per-source attributed hit rates, per-domain performance, conviction-band
 * calibration, per-pattern win rates). P17-safe: no dollar amounts — realized
 * P&L lives on the PIN-gated Holdings page. Standard module page pattern
 * (fetch + 120s refresh + ModuleContainer grid, per RiskEngine.jsx).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Wallet, Clock } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import { usePage } from '../contexts/PageContext.jsx';

const MUTED = '#6b7280';
const ACTION_COLOR = { acted: '#10b981', skipped: '#f59e0b', other: '#6b7280' };

function actionBucket(action) {
  const a = String(action || '').toLowerCase();
  if (a.startsWith('acted')) return 'acted';
  if (a.startsWith('skipped')) return 'skipped';
  return 'other';
}

function fmtAge(iso) {
  if (!iso) return '-';
  // SQLite timestamps are 'YYYY-MM-DD HH:MM:SS' UTC — normalize to strict ISO
  // ('T' separator + 'Z') so Date parsing works across browsers.
  const norm = iso.includes('T') ? iso : iso.replace(' ', 'T');
  const d = new Date(norm.endsWith('Z') || norm.includes('+') ? norm : norm + 'Z');
  if (isNaN(d.getTime())) return '-';
  const ms = Date.now() - d.getTime();
  if (ms < 3_600_000) return `${Math.max(1, Math.floor(ms / 60_000))}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

function pct(v) {
  return v == null ? '-' : `${Math.round(v * 100)}%`;
}

function rateColor(v) {
  if (v == null) return MUTED;
  if (v >= 0.55) return '#10b981';
  if (v >= 0.4) return '#f59e0b';
  return '#ef4444';
}

function ActionBadge({ action }) {
  const b = actionBucket(action);
  const color = ACTION_COLOR[b];
  return (
    <span className="text-[9px] font-mono font-bold uppercase px-1.5 py-0.5 rounded" style={{ color, backgroundColor: `${color}1a` }}>
      {b}
    </span>
  );
}

function JournalSection({ journal, behavior }) {
  const entries = journal?.entries || [];
  const summary = behavior?.summary;
  const byRegime = behavior?.by_regime || {};
  return (
    <ModuleContainer title="Decision Journal" subtitle={`operator actions on signals + KAIROS recommendations · ${journal?.total ?? 0} total`}>
      {summary && (
        <div className="flex items-center gap-4 flex-wrap mb-3 text-[11px] font-mono">
          <span><span className="text-korinth-muted">All-time:</span> <span className="text-emerald-400 font-bold">{summary.all_time.acted} acted</span> · <span className="text-amber-400 font-bold">{summary.all_time.skipped} skipped</span></span>
          <span><span className="text-korinth-muted">30d:</span> <span className="text-emerald-400 font-bold">{summary.recent_30d.acted} acted</span> · <span className="text-amber-400 font-bold">{summary.recent_30d.skipped} skipped</span></span>
          {behavior?.legacy_recommendation_actions && (behavior.legacy_recommendation_actions.acted + behavior.legacy_recommendation_actions.skipped) > 0 && (
            <span className="text-korinth-muted">(+{behavior.legacy_recommendation_actions.acted + behavior.legacy_recommendation_actions.skipped} pre-journal rec actions)</span>
          )}
        </div>
      )}
      {Object.keys(byRegime).length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-3">
          {Object.entries(byRegime).map(([regime, c]) => (
            <span key={regime} className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-korinth-border bg-korinth-surface-2">
              <span className="text-korinth-muted">{regime}</span> <span className="text-emerald-400">{c.acted}</span>/<span className="text-amber-400">{c.skipped}</span>
            </span>
          ))}
        </div>
      )}
      {entries.length === 0 ? (
        <p className="text-[11px] font-mono text-korinth-muted">No journal entries yet. Acting or skipping a signal or KAIROS recommendation records a decision here.</p>
      ) : (
        <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
          {entries.map((e) => (
            <div key={e.id} className="flex items-start gap-2 rounded border border-korinth-border bg-korinth-surface-2 px-2 py-1.5">
              <ActionBadge action={e.action} />
              <div className="flex-1 min-w-0">
                <div className="text-[11px] text-korinth-text truncate">{e.reasoning || e.action}</div>
                <div className="text-[9.5px] font-mono text-korinth-muted">
                  {e.regime_at_decision ? `${e.regime_at_decision} · ` : ''}{fmtAge(e.created_at)}{e.pnl != null ? ` · pnl ${e.pnl > 0 ? '+' : ''}${e.pnl}%` : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </ModuleContainer>
  );
}

function SourceAttributionSection({ bySource }) {
  const rows = Array.isArray(bySource) ? bySource.filter(r => r.period_label === 'all_time') : [];
  const rows30 = Array.isArray(bySource) ? bySource.filter(r => r.period_label === '30d') : [];
  const recent = Object.fromEntries(rows30.map(r => [r.source, r]));
  return (
    <ModuleContainer title="Alpha Attribution by Source" subtitle="which inputs earn their seat · attributed hit rate (prediction outcomes weighted by input attribution)">
      {rows.length === 0 ? (
        <p className="text-[11px] font-mono text-korinth-muted">No source track record yet (recomputed by the daily reflection).</p>
      ) : (
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-[9px] font-mono uppercase text-korinth-muted text-left">
              <th className="py-1">Source</th>
              <th className="py-1 text-right">Hit (all-time)</th>
              <th className="py-1 text-right">Hit (30d)</th>
              <th className="py-1 text-right">Contributed</th>
              <th className="py-1 text-right">Resolved</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.source} className="border-t border-korinth-border">
                <td className="py-1 font-mono text-korinth-text">{r.source}</td>
                <td className="py-1 text-right font-mono font-bold" style={{ color: rateColor(r.attributed_hit_rate) }}>{pct(r.attributed_hit_rate)}</td>
                <td className="py-1 text-right font-mono" style={{ color: rateColor(recent[r.source]?.attributed_hit_rate) }}>{pct(recent[r.source]?.attributed_hit_rate)}</td>
                <td className="py-1 text-right font-mono text-korinth-muted">{r.predictions_contributed_to ?? '-'}</td>
                <td className="py-1 text-right font-mono text-korinth-muted">{r.sample_size_resolved ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </ModuleContainer>
  );
}

function DomainSection({ byDomain }) {
  const all = byDomain?.windows?.all_time || [];
  const recent = Object.fromEntries((byDomain?.windows?.recent_30d || []).map(d => [d.domain, d]));
  return (
    <ModuleContainer title="Performance by Domain" subtitle={`prediction hit rates per asset domain · sample floor ${byDomain?.sample_floor ?? 5}`}>
      {all.length === 0 ? (
        <p className="text-[11px] font-mono text-korinth-muted">No resolved predictions yet.</p>
      ) : (
        <div className="space-y-1.5">
          {all.map((d) => (
            <div key={d.domain} className="flex items-center gap-2">
              <span className="w-20 text-[10px] font-mono text-korinth-muted uppercase">{d.domain}</span>
              <div className="flex-1 h-2.5 rounded bg-korinth-surface-2 overflow-hidden">
                {d.hit_rate != null && (
                  <div className="h-full rounded" style={{ width: `${Math.round(d.hit_rate * 100)}%`, backgroundColor: rateColor(d.hit_rate) }} />
                )}
              </div>
              <span className="w-12 text-right text-[10px] font-mono font-bold" style={{ color: rateColor(d.hit_rate) }}>{pct(d.hit_rate)}</span>
              <span className="w-20 text-right text-[9.5px] font-mono text-korinth-muted">n={d.sample_size}{recent[d.domain]?.hit_rate != null ? ` · 30d ${pct(recent[d.domain].hit_rate)}` : ''}</span>
            </div>
          ))}
          {Array.isArray(byDomain?.notes) && byDomain.notes.length > 0 && (
            <p className="text-[9.5px] font-mono text-korinth-muted italic pt-1">{byDomain.notes.join(' ')}</p>
          )}
        </div>
      )}
    </ModuleContainer>
  );
}

function CalibrationSection({ bands }) {
  const rows = Array.isArray(bands) ? bands : [];
  return (
    <ModuleContainer title="Conviction Calibration" subtitle="stated confidence vs actual hit rate">
      {rows.length === 0 ? (
        <p className="text-[11px] font-mono text-korinth-muted">No calibration metrics yet.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((m) => (
            <div key={m.period_window} className="rounded border border-korinth-border bg-korinth-surface-2 p-2">
              <div className="text-[10px] font-mono text-korinth-muted mb-1">
                {m.period_window} · overall {pct(m.hit_rate_overall)} over {m.total_predictions_resolved} resolved{m.trend ? ` · ${m.trend}` : ''}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {Object.entries(m.hit_rate_by_band || {}).map(([band, rate]) => (
                  <span key={band} className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-korinth-border">
                    <span className="text-korinth-muted">{band}%:</span> <span className="font-bold" style={{ color: rateColor(rate) }}>{pct(rate)}</span>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </ModuleContainer>
  );
}

function PatternsSection({ patterns }) {
  const rows = patterns?.rows || [];
  return (
    <ModuleContainer title="Pattern Win Rates" subtitle={`per prediction type and timeframe · win = correct + 0.5x partial · floor ${patterns?.sample_floor ?? 5}`}>
      {rows.length === 0 ? (
        <p className="text-[11px] font-mono text-korinth-muted">No resolved predictions yet.</p>
      ) : (
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-[9px] font-mono uppercase text-korinth-muted text-left">
              <th className="py-1">Pattern</th>
              <th className="py-1 text-right">Win rate</th>
              <th className="py-1 text-right">C / P / W</th>
              <th className="py-1 text-right">n</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.pattern} className="border-t border-korinth-border align-top">
                <td className="py-1 font-mono text-korinth-text">
                  {p.pattern}
                  {p.best_conditions && <div className="text-[9px] text-korinth-muted">best: {p.best_conditions}{p.worst_conditions ? ` · worst: ${p.worst_conditions}` : ''}</div>}
                </td>
                <td className="py-1 text-right font-mono font-bold" style={{ color: rateColor(p.win_rate) }}>{p.graded ? pct(p.win_rate) : 'n/a'}</td>
                <td className="py-1 text-right font-mono text-korinth-muted">{p.n_correct}/{p.n_partial}/{p.n_wrong}</td>
                <td className="py-1 text-right font-mono text-korinth-muted">{p.sample_size}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </ModuleContainer>
  );
}

export default function WealthCommand() {
  const { setPage } = usePage();
  useEffect(() => { setPage('wealth'); }, []);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await fetch('/api/wealth');
      const d = await r.json();
      if (!r.ok) { setErr(d.error || `HTTP ${r.status}`); return; }
      setData(d);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 120000);
    return () => clearInterval(iv);
  }, [load]);

  const attribution = data?.attribution || {};

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-korinth-accent/10 border border-korinth-accent/30 flex items-center justify-center text-korinth-accent">
            <Wallet className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Wealth Command</h1>
            <p className="text-[10px] font-mono text-korinth-muted">decision journal · alpha attribution · outcome-based (no dollars; see Holdings for P&L)</p>
          </div>
        </div>
        {data?.as_of && (
          <div className="flex items-center gap-1.5 text-[10px] font-mono text-korinth-muted">
            <Clock className="w-3 h-3" /> {new Date(data.as_of).toISOString().slice(0, 16).replace('T', ' ')} UTC
          </div>
        )}
      </div>

      {loading && !data && <div className="text-[11px] font-mono text-korinth-muted animate-pulse">Loading wealth command…</div>}
      {err && <div className="text-[11px] font-mono text-korinth-danger">Error: {err} <button onClick={load} className="ml-2 underline">retry</button></div>}

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <JournalSection journal={data.journal} behavior={data.behavior} />
          <SourceAttributionSection bySource={attribution.by_source} />
          <DomainSection byDomain={attribution.by_domain} />
          <div className="space-y-4">
            <CalibrationSection bands={attribution.by_conviction_band} />
            <PatternsSection patterns={attribution.patterns} />
          </div>
        </div>
      )}

      <p className="text-[10px] font-mono text-korinth-muted italic border-t border-korinth-border pt-2">
        Attribution here is outcome-based (hit rates, percentages) — no dollar amounts per P17. Dollar-level realized performance lives on <Link to="/holdings" className="text-korinth-accent underline">Holdings</Link>.
      </p>
    </div>
  );
}
